import { useQueryClient } from '@tanstack/react-query';

import useAIJobChannel from 'hooks/useAIJobChannel';
import { Jobbable } from 'lib/aiJobHelpers';
import { clone } from 'lib/util';
import AIJob from 'models/AIJob';
import Attachment from 'models/Attachment';
import Admission from 'models/reviews/Admission';
import Authorization from 'models/reviews/Authorization';
import Referral from 'models/reviews/Referral';
import Summarization from 'models/Summarization';
import { admissionQueryKeys } from 'services/api/admissions';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { summarizationQueryKeys } from 'services/api/summarizations';

function getJobbableConfig(jobbable: Jobbable) {
  if (jobbable instanceof Summarization) return { queryKeys: summarizationQueryKeys, klass: Summarization };
  if (jobbable instanceof Authorization) return { queryKeys: authorizationQueryKeys, klass: Authorization };
  if (jobbable instanceof Admission) return { queryKeys: admissionQueryKeys, klass: Admission };
  return { queryKeys: referralQueryKeys, klass: Referral };
}

type JobbableAttachmentJobSubscriberProps = {
  attachment: Attachment;
  jobbable: Jobbable;
};

export default function JobbableAttachmentJobSubscriber({
  attachment,
  jobbable,
}: JobbableAttachmentJobSubscriberProps) {
  const queryClient = useQueryClient();
  const job = attachment.extractDataAIJob;

  const { queryKeys, klass } = getJobbableConfig(jobbable);
  useAIJobChannel({
    params: {
      ai_job_id: job?.isProcessing ? job.id : undefined,
      attachable_model: jobbable instanceof Admission ? 'Review::Admission' : undefined,
    },
    onMessageReceived: (data) => {
      const updatedJob = new AIJob(data);
      if (updatedJob.isProcessing) return;
      queryClient.setQueryData(queryKeys.show({ id: jobbable.id }), (oldData: Jobbable) => {
        if (!oldData?.attachments) return oldData;

        const updatedAttachments = oldData.attachments.map((att) => {
          if (att.id === data.aiJobbableId) att.extractDataAIJob = updatedJob;
          return att;
        });

        return clone(klass, oldData, { attachments: updatedAttachments });
      });
    },
  });

  return null;
}
